import api from '@/utils/ky';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { SkillsAssessment } from '../role-play/session-feedback/skill-assessment/skills-assessment';
import { SummaryContent } from '../role-play/session-feedback/summary/summary-content';
import {
  TabsComponent,
  type TabItem,
} from '../role-play/session-feedback/tabs';
import { TranscriptAnalysis } from '../role-play/session-feedback/transcript/transcript-analysis';

export function CoachRoleplayFeedback() {
  const { t } = useTranslation();
  const { lang } = useParams();

  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  const [summaryData, setSummaryData] = useState<any>(null);

  const { mutate: getRoleplaySessionSummary } = useMutation({
    mutationFn: (data: { sessionId: string; language: string }) =>
      api
        .post('api/coach/roleplay/feedback', {
          json: data,
          timeout: 60000,
        })
        .json<{ success: boolean; sessionData: any }>(),
    onSuccess: (data) => {
      console.log('roleplay summary data: ', data);
      setSummaryData(data);
    },
    onError: (error: any) => {
      toast.error(t('feedback.error.load_summary', 'Could not get roleplay summary data.'));
    },
  });

  useEffect(() => {
    if (sessionId) {
      getRoleplaySessionSummary({ sessionId, language: lang });
    } else {
      toast.error(t('feedback.error.session_not_found', 'Session ID not found.'));
    }
  }, [sessionId, lang, t]);

  const tabs: TabItem[] = [
    {
      value: 'summary',
      label: t('feedback.tabs.summary', 'Summary'),
      content: <SummaryContent summary={summaryData?.summary} />,
    },
    {
      value: 'skills-assessment',
      label: t('feedback.tabs.skills_assessment', 'Skills assessment'),
      content: <SkillsAssessment assessments={summaryData?.skillsAssessment} />,
    },
    {
      value: 'transcript',
      label: t('feedback.tabs.transcript', 'Transcript analysis'),
      content: (
        <TranscriptAnalysis transcript={summaryData?.transcriptAnalysis} />
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      <div className="bg-white border-b border-[#e0e0e0]">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="/favicon.ico"
              alt={t('feedback.roleplay_feedback_icon', 'Roleplay feedback icon')}
              className="h-6 w-6"
            />
            <h1 className="text-lg font-medium">
              {t('feedback.roleplay_feedback', 'Roleplay feedback')}
            </h1>
          </div>
        </div>
      </div>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 lg:p-8">
          <TabsComponent tabs={tabs} defaultValue="summary" />
        </div>
      </div>
    </div>
  );
}
